import React, { useEffect, useState, useRef } from 'react';
import comms from './comms';
import { useConnectionStatus } from '../hooks/useOfflineData';
import { connectionManager } from '../services/connectionManager';

/**
 * Connection Status Indicator
 * 
 * Displays a small circular icon showing the current socket connection status.
 * - Green: Connected (no pending actions)
 * - Blue: Connected with pending sync (pulsing)
 * - Amber: Syncing/Reconnecting (with pulse animation)
 * - Orange: Offline with pending actions
 * - Red: Disconnected/Error/Failed
 * 
 * Shows badge with pending action count when offline or syncing.
 * - Single click: Reconnect (when disconnected) or force sync (when pending)
 * - Double click: Clear all pending actions (recovery for stuck queue)
 */

const statusColors = {
    connected: '#4CAF50',      // Green - fully online, no pending
    connectedPending: '#2196F3', // Blue - online with pending actions
    syncing: '#2196F3',        // Blue - actively syncing
    offline: '#FF9800',        // Orange - offline with cached data
    reconnecting: '#FFC107',   // Amber - attempting reconnect
    disconnected: '#9E9E9E',   // Gray - no connection (less alarming)
    error: '#F44336',          // Red - connection error
    failed: '#F44336'          // Red - connection failed
};

const getStatusTooltip = (status, pendingCount, criticalPending, isSyncing) => {
    if (isSyncing) {
        return `Syncing ${pendingCount} actions...`;
    }
    
    const tooltips = {
        connected: 'Connected to server',
        connectedPending: `Connected - ${pendingCount} pending sync - double-tap to clear`,
        syncing: `Syncing ${pendingCount} actions...`,
        offline: pendingCount > 0 
            ? `Offline - ${pendingCount} actions queued (will sync when online)` 
            : 'Offline - changes saved locally',
        reconnecting: 'Reconnecting...',
        disconnected: pendingCount > 0 
            ? `Disconnected - ${pendingCount} actions queued` 
            : 'Disconnected',
        error: `Connection error${pendingCount > 0 ? ` - ${pendingCount} queued` : ''}`,
        failed: `Connection failed - tap to retry`
    };
    
    return tooltips[status] || tooltips.disconnected;
};

function ConnectionStatus({ style = {}, size = 12, showBadge = true }) {
    // Use new offline-aware hook
    const { online, syncing, pendingCount, criticalPending } = useConnectionStatus();
    
    // Also track legacy status for reconnect behavior
    const [legacyStatus, setLegacyStatus] = useState(comms.getConnectionStatus());
    
    // Double-click detection for clearing stuck actions
    const clickTimeoutRef = useRef(null);
    const clickCountRef = useRef(0);
    
    useEffect(() => {
        const unsubscribe = comms.onConnectionStatusChange((newStatus) => {
            setLegacyStatus(newStatus);
        });
        
        // Cleanup timeout on unmount
        return () => {
            unsubscribe();
            if (clickTimeoutRef.current) {
                clearTimeout(clickTimeoutRef.current);
            }
        };
    }, []);
    
    // Determine display status based on combined state
    const getDisplayStatus = () => {
        if (syncing) return 'syncing';
        if (online && pendingCount > 0) return 'connectedPending';
        if (online) return 'connected';
        if (!online && pendingCount > 0) return 'offline';
        if (legacyStatus === 'reconnecting') return 'reconnecting';
        if (legacyStatus === 'failed') return 'failed';
        if (legacyStatus === 'error') return 'error';
        return 'disconnected';
    };
    
    const displayStatus = getDisplayStatus();
    
    const handleClick = () => {
        clickCountRef.current++;
        
        // Clear pending timeout
        if (clickTimeoutRef.current) {
            clearTimeout(clickTimeoutRef.current);
        }
        
        // Check for double-click (within 300ms)
        if (clickCountRef.current >= 2) {
            clickCountRef.current = 0;
            // Double-click: Clear all pending actions (recovery)
            if (pendingCount > 0) {
                if (window.confirm(`Clear ${pendingCount} pending actions? This cannot be undone.`)) {
                    console.log('[ConnectionStatus] User confirmed clearing all pending actions');
                    connectionManager.clearAllPendingActions();
                }
            }
            return;
        }
        
        // Set timeout for single-click action
        clickTimeoutRef.current = setTimeout(() => {
            clickCountRef.current = 0;
            
            // Single click: Reconnect or force sync
            // Allow manual reconnect when disconnected/failed/error
            if (!online || displayStatus === 'failed' || displayStatus === 'error' || displayStatus === 'disconnected') {
                comms.triggerReconnect();
                connectionManager.reconnect();
            }
            // Allow force sync when online with pending
            else if (online && pendingCount > 0 && !syncing) {
                connectionManager.forceSync();
            }
        }, 300);
    };
    
    const isAnimating = displayStatus === 'reconnecting' || displayStatus === 'syncing' || displayStatus === 'connectedPending';
    const isClickable = !online || pendingCount > 0 || displayStatus === 'failed' || displayStatus === 'error';
    
    const tooltip = getStatusTooltip(displayStatus, pendingCount, criticalPending, syncing);
    const color = statusColors[displayStatus] || statusColors.disconnected;
    
    return (
        <div
            title={tooltip}
            onClick={handleClick}
            style={{
                position: 'relative',
                display: 'inline-flex',
                alignItems: 'center',
                cursor: isClickable ? 'pointer' : 'default',
                ...style
            }}
        >
            {/* Main indicator dot */}
            <div
                style={{
                    width: size,
                    height: size,
                    borderRadius: '50%',
                    backgroundColor: color,
                    animation: isAnimating ? 'connectionPulse 1.5s infinite' : 'none',
                    boxShadow: `0 0 4px ${color}`,
                    transition: 'background-color 0.3s ease',
                }}
            />
            
            {/* Pending count badge */}
            {showBadge && pendingCount > 0 && (
                <div
                    style={{
                        position: 'absolute',
                        top: -4,
                        right: -8,
                        minWidth: 14,
                        height: 14,
                        borderRadius: 7,
                        backgroundColor: criticalPending > 0 ? '#F44336' : '#FF9800',
                        color: 'white',
                        fontSize: 9,
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '0 3px',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.3)',
                    }}
                >
                    {pendingCount > 99 ? '99+' : pendingCount}
                </div>
            )}
        </div>
    );
}

/**
 * Extended Connection Status with text label
 * Shows status text alongside the indicator
 */
export function ConnectionStatusExtended({ style = {}, size = 12 }) {
    const { online, syncing, pendingCount, criticalPending } = useConnectionStatus();
    
    const getStatusText = () => {
        if (syncing) return `Syncing (${pendingCount})`;
        if (online && pendingCount > 0) return `Online (${pendingCount} pending)`;
        if (online) return 'Online';
        if (pendingCount > 0) return `Offline (${pendingCount} pending)`;
        return 'Offline';
    };
    
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, ...style }}>
            <ConnectionStatus size={size} showBadge={false} />
            <span style={{ 
                fontSize: 11, 
                color: online ? '#4CAF50' : '#F44336',
                fontWeight: 500 
            }}>
                {getStatusText()}
            </span>
        </div>
    );
}

export default ConnectionStatus;
